import format from 'string-template';

import { EditorActionType } from '../../utils';
import {
  GenerationConfig,
  ClientLevelOptions,
  SessionDurationOptions,
  ExerciseFormatOptions,
  PerformedLocationOptions,
  IntensityLevelOptions,
} from '../constants';

import texts from './texts';

const ConversationType = {
  USER: 'user',
  ASSISTANT: 'assistant',
};

const generateAIWorkout = async ({
  remote,
  prompt,
  requestedBy,
  coachId,
  conversationHistory = [],
  currentActivities = [],
  workoutRequestId,
}) => {
  try {
    const response = await remote('workout-generateAIWorkout', {
      prompt,
      requestedBy,
      coachId,
      conversationHistory,
      currentActivities,
      useJudge: true,
      ...(workoutRequestId && { requestId: workoutRequestId }),
    }, { method: 'POST' });
    const {
      success, error, workout, questions, requestId,
    } = await response.json();
    return {
      success, error, workout, questions, requestId,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fill missing exercise data for an activity.
 * @param {Object} activity - The activity to fill missing data for.
 * @returns {Object} The activity with the missing data filled.
 */
const fillMissingExerciseData = (exercises, activity) => {
  if (activity.exerciseId && activity.type !== EditorActionType.REST && activity.type !== EditorActionType.CIRCUIT) {
    const exercise = exercises.find((ex) => ex.id === activity.exerciseId);

    if (!exercise) {
      throw new Error(`
        Exercise ID: ${activity.exerciseId} in the AI response not found in the available exercises list.
        Please try again.
      `);
    }
    if (exercise) {
      return {
        ...activity,
        description: exercise.description,
        video: exercise.video,
        videoUrl: exercise.videoUrl,
        videoPreviewUrl: exercise.videoPreviewUrl || exercise.videoUrl,
        videoPreviewThumbnail: exercise.videoPreviewThumbnail,
        videoPreviewHighResThumbnail: exercise.videoPreviewHighResThumbnail,
        tags: exercise.tags,
      };
    }
  }

  // For REST activities, set default values
  if (activity.type === EditorActionType.REST) {
    return activity;
  }

  // For CIRCUIT activities, recursively process nested activities
  if (activity.type === EditorActionType.CIRCUIT && activity.activities) {
    return {
      ...activity,
      activities: activity.activities.map((act) => fillMissingExerciseData(exercises, act)),
    };
  }

  return activity;
};

/**
 * Build the initial prompt for the AI workout generation based on the
 * generation configurations and additional instructions.
 *
 * @param {Object} generationConfigs - The generation configurations.
 * @param {string} additionalInstructions - The additional instructions for the workout.
 * @returns {string} The initial prompt for the AI workout generation.
 */
const buildInitialPrompt = (generationConfigs, additionalInstructions = '') => {
  const {
    [GenerationConfig.ClientLevel]: clientLevel,
    [GenerationConfig.SessionDuration]: sessionDuration,
    [GenerationConfig.PerformedLocation]: performedLocation,
    [GenerationConfig.IntensityLevel]: intensityLevel,
    [GenerationConfig.ExerciseFormat]: exerciseFormat,
    [GenerationConfig.WorkoutStyle]: workoutStyle,
    [GenerationConfig.FocusAreas]: focusAreas,
    [GenerationConfig.AccessibleEquipment]: accessibleEquipment,
    [GenerationConfig.Limitations]: limitations,
  } = generationConfigs;

  return format(texts.initialPrompt, {
    clientLevel: ClientLevelOptions.find((option) => option.value === clientLevel)?.label,
    sessionDuration: SessionDurationOptions.find((option) => option.value === sessionDuration)?.label,
    performedLocation: PerformedLocationOptions.find((option) => option.value === performedLocation)?.label,
    intensityLevel: IntensityLevelOptions.find((option) => option.value === intensityLevel)?.label,
    exerciseFormat: ExerciseFormatOptions.find((option) => option.value === exerciseFormat)?.label,
    workoutStyle,
    focusAreas,
    accessibleEquipment,
    limitations,
    additionalInstructions,
  });
};

export {
  ConversationType,
  generateAIWorkout,
  fillMissingExerciseData,
  buildInitialPrompt,
};
